import { Injectable, inject } from '@angular/core';
import { MemoizedSelector, Store } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';

import type { Person } from '@ninety/platform/models/_shared/person';
import type { Team } from '@ninety/platform/models/_shared/team';
import type { User } from '@ninety/platform/models/_shared/user';
import type { UserSettings } from '@ninety/platform/models/_shared/user-settings';
import type { Company } from '@ninety/platform/models/company/company';
import type { CompanyUserListModel } from '@ninety/platform/models/company-users/company-user-list-model';
import type { CustomLanguage } from '@ninety/platform/models/language/custom-language';
import type { TeamListModel } from '@ninety/platform/models/team-list/team-list.model';
import type { BillingStatusReader } from '@ninety/platform/services/tokens/billing-status-reader.token';
import type { CompanyReader } from '@ninety/platform/services/tokens/company-reader.token';
import type { CompanyUsersReader } from '@ninety/platform/services/tokens/company-users-reader.token';
import type { CurrentPersonReader } from '@ninety/platform/services/tokens/current-person-reader.token';
import type { CurrentUserReader } from '@ninety/platform/services/tokens/current-user-reader.token';
import type { LanguageReader } from '@ninety/platform/services/tokens/language-reader.token';
import type { NotificationsReader } from '@ninety/platform/services/tokens/notifications-reader.token';
import type { TeamListReader } from '@ninety/platform/services/tokens/team-list-reader.token';

import { selectActiveInactiveCompanyUsers } from '../app-entities/company-users/company-users-state.selectors';
import { TeamSelectors } from '../app-entities/team-list/team-list-state.selectors';
import {
  selectCurrentUser,
  selectCurrentUserId,
  selectCurrentUserIsAdminOrOwner,
  selectCurrentUserIsObserver,
  selectHasSubscription,
  selectIsImplementer,
  selectIsImplementerFree,
  selectIsStripeStatusActive,
  selectRawCurrentUser,
  selectUserHasTeams,
  selectUserSettings,
} from '../app-entities/users/users-state.selectors';
import { selectCompany, selectCompanyId } from '../app-global/company/company-state.selectors';
import { selectCurrentPerson, selectCurrentPersonId } from '../app-global/current-person/current-person.selectors';
import { selectLanguage } from '../app-global/language/language.selectors';
import { selectNotificationsEnabled } from '../app-global/notifications/notification.selectors';

/**
 * Concrete `platform/state` implementation of the read-only "reader" abstractions that services
 * in `platform/services` inject (DEV-26105). Severing the `platform/services -> platform/state`
 * cycle: those services can no longer import state selectors directly, so they inject a reader
 * token whose interface lives in `platform/services`; the app composition root binds each token to
 * this class via `useExisting`.
 *
 * As more service back-edges are inverted, this class implements additional reader interfaces.
 */
@Injectable({ providedIn: 'root' })
export class PlatformServicesStateReader
  implements
    CurrentUserReader,
    CompanyReader,
    NotificationsReader,
    CurrentPersonReader,
    BillingStatusReader,
    TeamListReader,
    CompanyUsersReader,
    LanguageReader
{
  private readonly store = inject(Store);

  readonly currentUser$: Observable<User | undefined> = this.store.select(selectCurrentUser);
  readonly rawCurrentUser$: Observable<User | undefined> = this.store.select(selectRawCurrentUser);
  readonly company$: Observable<Company | null> = this.store.select(selectCompany);
  readonly notificationsEnabled$: Observable<boolean> = this.store.select(selectNotificationsEnabled);
  readonly filterBarTeamId$: Observable<string | undefined> = this.store.select(TeamSelectors.selectFilterBarTeamId);
  readonly filterBarTeam$: Observable<Team | null> = this.store.select(TeamSelectors.selectFilterBarTeam);
  readonly allTeams$: Observable<TeamListModel[]> = this.store.select(TeamSelectors.selectAll);

  readonly isStripeStatusActive$: Observable<boolean> = this.store
    .select(selectIsStripeStatusActive)
    .pipe(map(Boolean));
  readonly isImplementerFree$: Observable<boolean> = this.store.select(selectIsImplementerFree).pipe(map(Boolean));
  readonly isImplementer$: Observable<boolean> = this.store.select(selectIsImplementer).pipe(map(Boolean));
  readonly hasSubscription$: Observable<boolean> = this.store.select(selectHasSubscription).pipe(map(Boolean));
  readonly isAdminOrOwner$: Observable<boolean> = this.store.select(selectCurrentUserIsAdminOrOwner).pipe(map(Boolean));

  getIsStripeStatusActive(): boolean {
    return Boolean(this.snapshot(selectIsStripeStatusActive));
  }

  getCurrentUser(): User | undefined {
    return this.snapshot(selectCurrentUser);
  }

  getCurrentUserId(): string | null {
    return this.snapshot(selectCurrentUserId);
  }

  getCurrentUserIsObserver(): boolean {
    return Boolean(this.snapshot(selectCurrentUserIsObserver));
  }

  getUserHasTeams(): boolean {
    return Boolean(this.snapshot(selectUserHasTeams));
  }

  getUserSettings(): UserSettings | undefined {
    return this.snapshot(selectUserSettings);
  }

  getLanguage(): CustomLanguage | null {
    return this.snapshot(selectLanguage);
  }

  getCompany(): Company | null {
    return this.snapshot(selectCompany);
  }

  getCompanyId(): string | undefined {
    return this.snapshot(selectCompanyId);
  }

  getCurrentPerson(): Person | undefined {
    return this.snapshot(selectCurrentPerson);
  }

  getCurrentPersonId(): string | undefined {
    return this.snapshot(selectCurrentPersonId);
  }

  getActiveInactiveCompanyUsers(): CompanyUserListModel[] {
    return this.snapshot(selectActiveInactiveCompanyUsers);
  }

  /** Read a selector's current value synchronously. */
  private snapshot<T>(selector: MemoizedSelector<object, T>): T {
    let value: T;
    this.store
      .select(selector)
      .pipe(take(1))
      .subscribe(v => (value = v));
    return value;
  }
}
